import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { aroundIdResetKeyChange } from "@/hooks/aroundIdPagination";

const HIGHLIGHT_ROW_PARAM = "highlightRow";
const ASSIGNMENT_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isCommitteeAssignmentAroundId(value: string | null | undefined): value is string {
  return Boolean(value && ASSIGNMENT_UUID.test(value));
}

function aroundIdFromSearchParams(searchParams: URLSearchParams) {
  const highlightRow = searchParams.get(HIGHLIGHT_ROW_PARAM);
  return isCommitteeAssignmentAroundId(highlightRow) ? highlightRow : undefined;
}

interface UseCommitteeAssignmentAroundIdOptions {
  resetKey?: string;
}

export function useCommitteeAssignmentAroundId({ resetKey }: UseCommitteeAssignmentAroundIdOptions = {}) {
  const [searchParams] = useSearchParams();
  const [aroundId, setAroundId] = useState<string | undefined>(() => aroundIdFromSearchParams(searchParams));
  const previousResetKey = useRef(resetKey);
  const previousTarget = useRef(aroundIdFromSearchParams(searchParams));
  const keepAroundIdThroughNextResetRef = useRef(false);

  useEffect(() => {
    const nextAroundId = aroundIdFromSearchParams(searchParams);
    const resetChange = aroundIdResetKeyChange(previousResetKey, resetKey, keepAroundIdThroughNextResetRef);

    if (resetChange === "clear") {
      previousTarget.current = nextAroundId;
      setAroundId(undefined);
      return;
    }

    if (resetChange === "keep") {
      previousTarget.current = nextAroundId;
      return;
    }

    if (nextAroundId && nextAroundId !== previousTarget.current) {
      setAroundId(nextAroundId);
    }
    previousTarget.current = nextAroundId;
  }, [resetKey, searchParams]);

  const clearAroundId = useCallback(() => {
    keepAroundIdThroughNextResetRef.current = false;
    setAroundId(undefined);
  }, []);

  const keepAroundIdThroughNextReset = useCallback((keep = true) => {
    keepAroundIdThroughNextResetRef.current = keep;
  }, []);

  return { aroundId, clearAroundId, keepAroundIdThroughNextReset };
}

interface UseCommitteeAssignmentListDeepLinkOptions {
  loading: boolean;
  rowIds: string[];
  personIds?: string[];
  serverCurrentPage?: number;
  onTargetMissing?: () => void;
  hasBlockingFilters?: boolean;
  aroundId?: string;
}

export function useCommitteeAssignmentListDeepLink({
  loading,
  rowIds,
  personIds = [],
  serverCurrentPage,
  onTargetMissing,
  hasBlockingFilters = false,
  aroundId
}: UseCommitteeAssignmentListDeepLinkOptions) {
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [highlightRowId, setHighlightRowId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrolledHighlightIdRef = useRef<string | null>(null);
  const highlightRow = searchParams.get(HIGHLIGHT_ROW_PARAM);
  const hasTargetRow = !highlightRow || rowIds.includes(highlightRow) || personIds.includes(highlightRow);

  useEffect(() => {
    if (!highlightRow) {
      return;
    }

    if (loading || serverCurrentPage === undefined) {
      return;
    }

    if (
      !hasTargetRow &&
      (aroundId === highlightRow || (hasBlockingFilters && isCommitteeAssignmentAroundId(highlightRow)))
    ) {
      return;
    }

    const next = new URLSearchParams(searchParams);
    next.delete(HIGHLIGHT_ROW_PARAM);
    if (serverCurrentPage > 0) {
      next.set("currentPage", String(serverCurrentPage));
    } else {
      next.delete("currentPage");
    }
    const nextSearch = next.toString();
    if (nextSearch !== searchParams.toString()) {
      navigate({ pathname, search: nextSearch ? `?${nextSearch}` : "" }, { replace: true });
    }

    if (rowIds.includes(highlightRow) || personIds.includes(highlightRow)) {
      setHighlightRowId(highlightRow);
    } else {
      setHighlightRowId(null);
      onTargetMissing?.();
    }
  }, [
    aroundId,
    hasBlockingFilters,
    hasTargetRow,
    highlightRow,
    loading,
    navigate,
    onTargetMissing,
    pathname,
    personIds,
    rowIds,
    searchParams,
    serverCurrentPage
  ]);

  useEffect(() => {
    if (!highlightRowId) {
      scrolledHighlightIdRef.current = null;
      return;
    }

    if (scrolledHighlightIdRef.current === highlightRowId) {
      return;
    }

    let cancelled = false;
    let attempt = 0;
    let timeoutId: number | undefined;
    let rafId: number | undefined;

    const tryScroll = () => {
      if (cancelled) {
        return;
      }

      const row = containerRef.current?.querySelector("tr.row-highlight") as HTMLElement | null;
      if (row) {
        scrolledHighlightIdRef.current = highlightRowId;
        row.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }

      if (attempt++ >= 30) {
        return;
      }

      rafId = requestAnimationFrame(tryScroll);
    };

    timeoutId = window.setTimeout(tryScroll, 0);

    return () => {
      cancelled = true;
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
      if (rafId !== undefined) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [highlightRowId]);

  return { highlightRowId, containerRef };
}
